import { useTranslations } from 'next-intl';
import { type FC, useCallback, useLayoutEffect } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import clsx from 'clsx';
import { z } from 'zod';

import type { GateGroupDetails } from '@/app/[locale]/(authorized)/payment-gates/[id]/components/PaymentGate/hooks/use-gate-group-details';
import { FormSelect } from '@/components/common/form/FormSelect';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { CurrencySelect } from '@/components/common/select/CurrencySelect';
import { type GateGroupOption, GateGroupSelect } from '@/components/common/select/GateGroupSelect';
import { OperationPeriodSelect } from '@/components/common/select/OperationPeriodSelect';
import { type WithClassName } from '@/types/react';

import { DashboardV2PeriodType } from '../../../../../../../../../../../../../generated/graphql';

import styles from './SalesVolumeComparisonChartFilter.module.scss';

type SalesVolumeComparisonChartFilterProps = WithClassName & {
  defaultValues?: Partial<SalesVolumeComparisonChartFilter>;
  gateGroupDetails?: GateGroupDetails;
  onSubmit: SubmitHandler<SalesVolumeComparisonChartFilter>;
};

const EMPTY_GATE_GROUPS: SalesVolumeComparisonChartFilter['gateGroups'] = [];

const useFormSchema = (gateGroupDetails?: GateGroupDetails) => {
  const t = useTranslations('charts.filters');

  const gateGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    currency_id: z.optional(z.string()),
  });

  const gateGroupsSchema = z.array(gateGroupSchema);

  return z.object({
    period: z.nativeEnum(DashboardV2PeriodType),
    currency: z.object({
      id: z.string(),
      name: z.string(),
    }),
    gateGroup: gateGroupDetails ? gateGroupSchema : z.optional(gateGroupSchema),
    gateGroups: gateGroupDetails ? gateGroupsSchema : gateGroupsSchema.length(2, t('gateGroup.errors.selectTwoGates')),
  });
};

export type SalesVolumeComparisonChartFilter = z.infer<ReturnType<typeof useFormSchema>>;

export const SalesVolumeComparisonChartFilter: FC<SalesVolumeComparisonChartFilterProps> = (props) => {
  const t = useTranslations('charts.filters');
  const tButtons = useTranslations('buttons');

  const { className, defaultValues, gateGroupDetails, onSubmit } = props;

  const form = useForm<SalesVolumeComparisonChartFilter>({
    resolver: zodResolver(useFormSchema(gateGroupDetails)),
    defaultValues,
  });

  const { handleSubmit, register, setValue, watch } = form;

  const [currency, gateGroups] = watch(['currency', 'gateGroups']);
  const safeGateGroups = gateGroups ?? EMPTY_GATE_GROUPS;

  const gateGroupsFilter = useCallback(
    (option: GateGroupOption) => {
      return (!currency?.id || option.currency_id === currency.id) && option.id !== gateGroupDetails?.id;
    },
    [currency?.id, gateGroupDetails?.id]
  );

  useLayoutEffect(() => {
    if (!currency?.id) {
      return;
    }

    const filteredGroups = safeGateGroups.filter(({ currency_id }) => currency_id === currency.id);

    if (filteredGroups.length !== safeGateGroups.length) {
      setValue('gateGroups', filteredGroups);
    }
  }, [currency?.id, safeGateGroups, setValue]);

  return (
    <FormProvider {...form}>
      <form className={clsx(styles.root, className)} onSubmit={handleSubmit(onSubmit)}>
        <FormSelect
          {...register('period')}
          variant="box"
          label={t('period.label')}
          component={OperationPeriodSelect}
          withHalfYear={false}
        />

        {gateGroupDetails ? (
          <FormSelect
            {...register('gateGroup')}
            variant="box"
            label={t('gateGroup.label')}
            component={GateGroupSelect}
            filter={gateGroupsFilter}
          />
        ) : (
          <>
            <FormSelect
              {...register('currency')}
              variant="box"
              label={t('currency.label')}
              component={CurrencySelect}
            />

            <FormSelect
              {...register('gateGroups')}
              variant="box"
              isMulti
              label={t('gateGroup.label')}
              placeholder={t('gateGroup.placeholder.twoGates')}
              component={GateGroupSelect}
              filter={gateGroupsFilter}
            />
          </>
        )}

        <FormSubmitButton className={styles.submitButton}>{tButtons('search')}</FormSubmitButton>
      </form>
    </FormProvider>
  );
};
